// @ts-nocheck
// Estudo do livro — feed por marcos com 7 tipos de post.
// PLUGADO no backend: milestones e posts vêm via props do App.tsx.
// Mantém STUDY_FILTERS / POST_TYPES como constantes locais (UI).
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Icon } from '../icons';
import { toast } from '../toast';
import { useTerm, Avatar } from './components';
import {
  STUDY_FILTERS, POST_TYPES,
  MilestoneInsightFinal, Milestone,
} from './data';
import { api } from '../../services/apiV2';

// ReactionRow interativa local — usa callback do server (onReact) e mostra
// estado "ativa" se o viewer já reagiu (yourReactions).
const InteractiveReactionRow: React.FC<{
  postId: string;
  reactions: { tocou: number; aprendi: number; fogo: number; salvar: number };
  yourReactions?: string[];
  onReact?: (postId: string, kind: 'tocou' | 'aprendi' | 'fogo' | 'salvar') => Promise<void>;
}> = ({ postId, reactions, yourReactions = [], onReact }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [optimistic, setOptimistic] = useState<{ counts: any; yours: string[] } | null>(null);

  const eff = optimistic || { counts: reactions, yours: yourReactions };
  const items: { key: 'tocou' | 'aprendi' | 'fogo' | 'salvar'; emoji: string }[] = [
    { key: 'tocou',   emoji: '🙏' },
    { key: 'aprendi', emoji: '💡' },
    { key: 'fogo',    emoji: '🔥' },
    { key: 'salvar',  emoji: '📌' },
  ];

  const click = async (kind: typeof items[number]['key']) => {
    if (!onReact || busy) return;
    setBusy(kind);
    const wasActive = eff.yours.includes(kind);
    // Otimistico
    setOptimistic({
      counts: { ...eff.counts, [kind]: Math.max(0, eff.counts[kind] + (wasActive ? -1 : 1)) },
      yours: wasActive ? eff.yours.filter(k => k !== kind) : [...eff.yours, kind],
    });
    try { await onReact(postId, kind); }
    catch { setOptimistic(null); /* reverte */ }
    finally { setBusy(null); }
  };

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {items.map(it => {
        const active = eff.yours.includes(it.key);
        return (
          <button key={it.key} onClick={() => click(it.key)} disabled={busy !== null} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '5px 9px', borderRadius: 999,
            background: active ? 'var(--ember-soft)' : 'var(--paper)',
            border: '1px solid ' + (active ? 'var(--ember)' : 'var(--line)'),
            fontSize: 11, color: active ? 'var(--ember)' : 'var(--ink-2)',
            fontFamily: 'Geist, sans-serif',
            transition: 'all 160ms',
            cursor: busy ? 'wait' : 'pointer',
          }}>
            <span style={{ fontSize: 12 }}>{it.emoji}</span>
            <span className="mono" style={{ fontSize: 10, color: active ? 'var(--ember)' : 'var(--ink-3)', fontWeight: active ? 600 : 400 }}>
              {eff.counts[it.key] || 0}
            </span>
          </button>
        );
      })}
    </div>
  );
};

// ─── Toolbar compacta: trigger único de reação (popover) + botão de comentário ───
// Usado nos cards refinados (LeaderAnswer e futuros) — substitui a fileira de
// 4 chips de reação por 1 ícone que abre menu vertical, e move o "comentar"
// pra um ícone de chat na mesma linha.
const PostToolbar: React.FC<{
  postId: string;
  reactions: { tocou: number; aprendi: number; fogo: number; salvar: number };
  yourReactions?: string[];
  onReact?: (postId: string, kind: 'tocou' | 'aprendi' | 'fogo' | 'salvar') => Promise<void>;
  commentCount?: number;
  commentsExpanded?: boolean;
  onToggleComments?: () => void;
}> = ({ postId, reactions, yourReactions = [], onReact, commentCount = 0, commentsExpanded, onToggleComments }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [optimistic, setOptimistic] = useState<{ counts: any; yours: string[] } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const eff = optimistic || { counts: reactions, yours: yourReactions };
  const items: { key: 'tocou' | 'aprendi' | 'fogo' | 'salvar'; emoji: string; label: string }[] = [
    { key: 'tocou',   emoji: '🙏', label: 'Tocou' },
    { key: 'aprendi', emoji: '💡', label: 'Aprendi' },
    { key: 'fogo',    emoji: '🔥', label: 'Fogo' },
    { key: 'salvar',  emoji: '📌', label: 'Salvar' },
  ];

  const totalCount = items.reduce((s, it) => s + (eff.counts[it.key] || 0), 0);
  const yourFirst = items.find(it => eff.yours.includes(it.key));
  const mostPopular = items.reduce((max, it) => (eff.counts[it.key] || 0) > (eff.counts[max.key] || 0) ? it : max, items[0]);
  const trigger = yourFirst || (totalCount > 0 ? mostPopular : items[0]);
  const triggerActive = !!yourFirst;

  // Fecha o popover ao clicar fora
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 50);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [open]);

  const click = async (kind: typeof items[number]['key']) => {
    if (!onReact || busy) return;
    setBusy(kind);
    setOpen(false);
    const wasActive = eff.yours.includes(kind);
    setOptimistic({
      counts: { ...eff.counts, [kind]: Math.max(0, eff.counts[kind] + (wasActive ? -1 : 1)) },
      yours: wasActive ? eff.yours.filter(k => k !== kind) : [...eff.yours, kind],
    });
    try { await onReact(postId, kind); }
    catch { setOptimistic(null); }
    finally { setBusy(null); }
  };

  // Estilo base — pill compacta 28px (levemente menor que antes pra liberar
  // visualmente o card; ainda tappable em mobile com 28px+gap6).
  const pillStyle = (active: boolean): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '0 9px', height: 28,
    borderRadius: 999,
    background: active ? 'var(--ember-soft)' : 'var(--paper)',
    border: '1px solid ' + (active ? 'var(--ember)' : 'var(--line)'),
    cursor: 'pointer',
    transition: 'all 160ms',
    fontFamily: 'inherit',
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {/* Trigger de reação + popover vertical */}
      <div ref={ref} style={{ position: 'relative' }}>
        <button
          aria-label="Reagir"
          onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
          style={pillStyle(triggerActive)}
        >
          <span style={{ fontSize: 12.5, lineHeight: 1 }}>{trigger.emoji}</span>
          {totalCount > 0 && (
            <span className="mono" style={{
              fontSize: 9.5, fontWeight: triggerActive ? 600 : 500,
              color: triggerActive ? 'var(--ember)' : 'var(--ink-3)',
            }}>{totalCount}</span>
          )}
        </button>

        {open && (
          <div style={{
            position: 'absolute', bottom: 'calc(100% + 6px)', left: 0,
            background: 'var(--paper)',
            border: '1px solid var(--line)',
            borderRadius: 14, padding: 4,
            boxShadow: '0 12px 24px -6px rgba(0,0,0,0.18), 0 4px 8px -4px rgba(0,0,0,0.08)',
            display: 'flex', flexDirection: 'column', gap: 2,
            zIndex: 20, minWidth: 140,
          }}>
            {items.map(it => {
              const active = eff.yours.includes(it.key);
              const cnt = eff.counts[it.key] || 0;
              return (
                <button
                  key={it.key}
                  onClick={(e) => { e.stopPropagation(); click(it.key); }}
                  disabled={busy !== null}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 12, padding: '7px 10px', borderRadius: 10,
                    background: active ? 'var(--ember-soft)' : 'transparent',
                    border: 'none', cursor: busy ? 'wait' : 'pointer',
                    transition: 'background 120ms', fontFamily: 'inherit',
                  }}
                  onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = 'var(--paper-2)'; }}
                  onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <span style={{ fontSize: 15 }}>{it.emoji}</span>
                    <span style={{
                      fontSize: 12.5, fontWeight: active ? 600 : 500,
                      color: active ? 'var(--ember)' : 'var(--ink-2)',
                    }}>{it.label}</span>
                  </span>
                  {cnt > 0 && (
                    <span className="mono" style={{
                      fontSize: 10,
                      color: active ? 'var(--ember)' : 'var(--ink-3)',
                    }}>{cnt}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Botão de comentário — mesma altura do trigger de reação */}
      <button
        aria-label="Comentar"
        onClick={(e) => { e.stopPropagation(); onToggleComments?.(); }}
        style={pillStyle(!!commentsExpanded)}
      >
        <Icon name="chat" size={13} color={commentsExpanded ? 'var(--ember)' : 'var(--ink-2)'} stroke={1.8}/>
        {commentCount > 0 && (
          <span className="mono" style={{
            fontSize: 9.5, fontWeight: commentsExpanded ? 600 : 500,
            color: commentsExpanded ? 'var(--ember)' : 'var(--ink-3)',
          }}>{commentCount}</span>
        )}
      </button>
    </div>
  );
};

// Author row compacta — usada em TODOS os post cards do estudo pra padronizar
// o autor. Avatar 24px + nome + role + tempo numa única linha. Page tag opcional
// é empurrado pra direita (ex: cards de reflection/citation com `pág 100`).
const CompactAuthorRow: React.FC<{
  author: any;
  timeAgo?: string;
  page?: number;
  milestone?: string;
  /** F6: livro pessoal do autor (so populado em Avulso). Mostra mini-pill com capa+titulo. */
  authorBook?: { id: string; title: string; author: string; coverUrl?: string | null; coverHue?: number | null; coverStyle?: string | null } | null;
}> = ({ author, timeAgo, page, milestone, authorBook }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, lineHeight: 1.2 }}>
    <Avatar letter={author.avatar} size={24}/>
    <div style={{
      flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6,
      overflow: 'hidden',
    }}>
      <span style={{
        fontWeight: 600, color: 'var(--ink)', flexShrink: 0,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160,
      }}>{author.name}</span>
      {author.verified && (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="var(--ember)" style={{ flexShrink: 0 }}>
          <path d="M12 2l2.4 1.8 3 .2.6 3 2 2.2-1.4 2.7.4 3-2.7 1.4-1.4 2.7-3-.4-2.7 1.4-2.2-2-3-.6-.2-3L2 12l1.8-2.4 .2-3 3-.6 2.2-2L12 2z"/>
          <path d="M9 12l2 2 4-4" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
      <span className="mono" style={{
        fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '0.04em',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {author.role && <>· {author.role === 'Admin' ? 'Líder' : author.role} </>}· {timeAgo}
      </span>
    </div>
    {(page || milestone) && (
      <span style={{
        padding: '2px 7px', borderRadius: 6,
        background: 'var(--paper-3)',
        fontFamily: 'Geist Mono, monospace',
        fontSize: 9.5, letterSpacing: '0.08em',
        color: 'var(--ink-2)', flexShrink: 0,
      }}>
        pág {page}{milestone ? ` · ${milestone}` : ''}
      </span>
    )}
    {/* F6: mini-pill do livro pessoal do autor (Avulso). Capa 14x20 + titulo truncado. */}
    {authorBook && (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '2px 7px 2px 3px', borderRadius: 10,
        background: 'var(--paper-2)', border: '1px solid var(--line)',
        flexShrink: 0, maxWidth: 120,
      }} title={`${authorBook.title} - ${authorBook.author}`}>
        <span style={{
          width: 14, height: 20, borderRadius: 2, flexShrink: 0,
          background: authorBook.coverUrl
            ? `url(${authorBook.coverUrl}) center/cover`
            : `linear-gradient(135deg, oklch(0.7 0.09 ${authorBook.coverHue || 22}), oklch(0.4 0.10 ${authorBook.coverHue || 22}))`,
        }}/>
        <span style={{
          fontSize: 9.5, color: 'var(--ink-2)', fontStyle: 'italic',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{authorBook.title}</span>
      </span>
    )}
  </div>
);

// PostFooter — combina toolbar de reação/comentário (alinhada à direita) +
// CommentsBlock controlado. Usado por todos os tipos de post pra padronizar
// o footer (reactions, comentários) em todos os cards do estudo.
const PostFooter: React.FC<{
  post: any;
  onReact?: (postId: string, kind: 'tocou' | 'aprendi' | 'fogo' | 'salvar') => Promise<void>;
  clubId?: string;
  session?: any;
  canPost?: boolean;
  onCommentAdded?: (postId: string, delta: number) => void;
  viewerId?: string;
  viewerIsModerator?: boolean;
  /** Margem-top — default 12 (varia ligeiramente entre cards). */
  marginTop?: number;
}> = ({ post, onReact, clubId, session, canPost, onCommentAdded, viewerId, viewerIsModerator, marginTop = 12 }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* Toolbar à direita (justifyContent: flex-end) — ações secundárias do post */}
      <div style={{ marginTop, display: 'flex', justifyContent: 'flex-end' }}>
        <PostToolbar
          postId={post.id}
          reactions={post.reactions}
          yourReactions={post.yourReactions}
          onReact={onReact}
          commentCount={post.comments || 0}
          commentsExpanded={open}
          onToggleComments={() => setOpen(v => !v)}
        />
      </div>
      {/* Comentários controlados pelo trigger acima — sem botão interno duplicado */}
      <CommentsBlock
        count={post.comments}
        clubId={clubId} postId={post.id} session={session} canPost={canPost}
        viewerId={viewerId} viewerIsModerator={viewerIsModerator}
        onCountChange={(d) => onCommentAdded?.(post.id, d)}
        expanded={open}
        onExpandedChange={setOpen}
        hideTrigger
      />
    </>
  );
};

// ─── Pequenos componentes auxiliares ───
const TypeBadge: React.FC<{ kind: string; size?: 'sm' | 'md' }> = ({ kind, size = 'sm' }) => {
  const meta = (POST_TYPES as any)[kind] || POST_TYPES.reflection;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: size === 'sm' ? '2px 8px' : '4px 10px',
      borderRadius: 999,
      background: 'color-mix(in oklab, var(--ember) 14%, var(--paper-2))',
      border: '1px solid color-mix(in oklab, var(--ember) 35%, transparent)',
      color: 'var(--ember)',
      fontFamily: 'Geist Mono, monospace',
      fontSize: size === 'sm' ? 9.5 : 10.5,
      letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600,
    }}>
      <span style={{ fontSize: size === 'sm' ? 11 : 13 }}>{meta.icon}</span>
      {meta.label}
    </span>
  );
};

const PageTag: React.FC<{ page?: number; milestone?: string }> = ({ page, milestone }) => (
  <span style={{
    padding: '2px 7px', borderRadius: 6,
    background: 'var(--paper-3)',
    fontFamily: 'Geist Mono, monospace',
    fontSize: 9.5, letterSpacing: '0.08em',
    color: 'var(--ink-2)',
  }}>
    pág {page}{milestone ? ` · ${milestone}` : ''}
  </span>
);

const AuthorRow: React.FC<{ author: any; page?: number; timeAgo?: string; milestone?: string; large?: boolean }> = ({
  author, page, timeAgo, milestone, large = false,
}) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <Avatar letter={author.avatar} size={large ? 38 : 32}/>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: large ? 14 : 13, fontWeight: 500 }}>{author.name}</span>
        {author.verified && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="var(--ember)">
            <path d="M12 2l2.4 1.8 3 .2.6 3 2 2.2-1.4 2.7.4 3-2.7 1.4-1.4 2.7-3-.4-2.7 1.4-2.2-2-3-.6-.2-3L2 12l1.8-2.4 .2-3 3-.6 2.2-2L12 2z"/>
            <path d="M9 12l2 2 4-4" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
      <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '0.06em', marginTop: 2 }}>
        {author.role && `${author.role === 'Admin' ? 'Líder' : author.role} · `}{timeAgo}
      </div>
    </div>
    {(page || milestone) && <PageTag page={page} milestone={milestone}/>}
  </div>
);

// CommentsBlock: header clicável + (quando expandido) lista + input de comentário.
// Lazy load: só busca da API ao expandir pela primeira vez.
// Optimistic: comment recém-criado entra direto na lista; count incrementa via
// callback onCountChange pra que o pai (App.tsx → posts state) também sincronize.
//
// Permissão: backend bloqueia POST de não-membros (mesmo em clube open) — o input
// de comentário fica desabilitado quando canPost=false (pra não fingir UI de algo
// que vai falhar). canPost vem do parent (true por padrão; false quando viewer
// não tem membership).
const CommentsBlock: React.FC<{
  count?: number;
  clubId?: string;
  postId?: string;
  session?: any;
  canPost?: boolean;
  onCountChange?: (delta: number) => void;
  viewerId?: string;
  viewerIsModerator?: boolean;
  /** Controlled mode — quando definido, parent gerencia expanded. */
  expanded?: boolean;
  onExpandedChange?: (next: boolean) => void;
  /** Esconde o botão "comentar/N comentários" interno. Útil quando o parent já
      tem um trigger externo (ex: ícone de comment na toolbar). */
  hideTrigger?: boolean;
}> = ({
  count = 0, clubId, postId, session, canPost = true, onCountChange, viewerId, viewerIsModerator,
  expanded: expandedProp, onExpandedChange, hideTrigger = false,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? !!expandedProp : internalExpanded;
  const setExpanded = (next: boolean) => {
    if (isControlled) onExpandedChange?.(next);
    else setInternalExpanded(next);
  };
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [comments, setComments] = useState<any[]>([]);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const handleEditSave = async (commentId: string) => {
    if (!session || !clubId || !postId) return;
    const text = editDraft.trim();
    if (!text) return;
    try {
      const r = await api.updateComment(session, clubId, postId, commentId, text);
      setComments(prev => prev.map(c => c.id === commentId ? r.comment : c));
      setEditingId(null);
      setEditDraft('');
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao editar comentário');
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!session || !clubId || !postId) return;
    if (!window.confirm('Excluir este comentário?')) return;
    try {
      await api.deleteComment(session, clubId, postId, commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
      onCountChange?.(-1);
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao excluir comentário');
    }
  };

  // Carrega ao primeira expansão
  useEffect(() => {
    if (!expanded || loaded || !session || !clubId || !postId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await api.listComments(session, clubId, postId);
        if (!cancelled) {
          setComments(r.comments || []);
          setLoaded(true);
        }
      } catch (e) {
        console.error('[post/comments]', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [expanded, loaded, session, clubId, postId]);

  const handlePost = async (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!session || !clubId || !postId) return;
    const text = draft.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      const r = await api.createComment(session, clubId, postId, text);
      setComments(prev => [...prev, r.comment]);
      setDraft('');
      onCountChange?.(+1);
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao comentar');
    } finally {
      setPosting(false);
    }
  };

  const headerLabel = count === 0
    ? (expanded ? 'fechar' : 'comentar')
    : `${count} ${count === 1 ? 'comentário' : 'comentários'}`;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {!hideTrigger && (
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded(!expanded); }}
          style={{
            marginTop: 12, width: '100%', textAlign: 'left',
            padding: '8px 0', borderTop: '1px solid var(--line)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'transparent', cursor: 'pointer',
          }}
        >
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{headerLabel}</span>
          <span style={{ display: 'inline-flex', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 160ms' }}>
            <Icon name="chevron-down" size={14} color="var(--ink-4)"/>
          </span>
        </button>
      )}

      {expanded && (
        <div style={{ marginTop: 4, paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* INPUT no topo — escrever vem antes de ler. Quando user toca o
              ícone de comment, o cursor pode focar no input direto. */}
          {canPost && session && clubId && postId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePost(e); } }}
                onClick={e => e.stopPropagation()}
                placeholder="Escreva um comentário..."
                disabled={posting}
                style={{
                  flex: 1, minWidth: 0,
                  padding: '9px 14px', borderRadius: 999,
                  background: 'var(--paper-2)', border: '1px solid var(--line)',
                  fontSize: 12.5, outline: 'none', fontFamily: 'inherit', color: 'var(--ink)',
                }}
              />
              <button
                onClick={handlePost}
                disabled={!draft.trim() || posting}
                style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: draft.trim() && !posting ? 'var(--ember)' : 'var(--paper-3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: 'none', cursor: draft.trim() && !posting ? 'pointer' : 'default',
                  flexShrink: 0,
                  transition: 'background 160ms',
                }}
              >
                <Icon name="arrow-right" size={14} color={draft.trim() && !posting ? 'var(--paper)' : 'var(--ink-4)'}/>
              </button>
            </div>
          )}
          {!canPost && (
            <div style={{ fontSize: 11, color: 'var(--ink-4)', fontStyle: 'italic', textAlign: 'center', padding: '4px 0' }}>
              Entre no grupo pra comentar.
            </div>
          )}

          {/* Loading + empty state + LISTA de comentários — embaixo do input */}
          {loading && comments.length === 0 && (
            <div style={{ color: 'var(--ink-3)', fontSize: 12, padding: '4px 0' }}>Carregando…</div>
          )}
          {!loading && comments.length === 0 && (
            <div style={{
              padding: '10px 12px', borderRadius: 12,
              border: '1px dashed var(--line-strong)',
              color: 'var(--ink-3)', fontSize: 12, fontStyle: 'italic',
              textAlign: 'center',
            }}>
              {canPost ? 'Sem comentários ainda. Seja o primeiro.' : 'Sem comentários ainda.'}
            </div>
          )}
          {comments.map((c: any) => {
            const canManage = canManageItem(c.author?.id, viewerId, !!viewerIsModerator);
            const isEditing = editingId === c.id;
            return (
            <div key={c.id} style={{ display: 'flex', gap: 8 }}>
              <Avatar letter={c.author?.avatar || '?'} size={28}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                {isEditing ? (
                  <div style={{
                    padding: '8px 10px', borderRadius: 14, borderTopLeftRadius: 4,
                    background: 'var(--paper-2)',
                    border: '1px solid var(--ember)',
                  }}>
                    <textarea
                      value={editDraft}
                      onChange={e => setEditDraft(e.target.value)}
                      autoFocus
                      rows={2}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        background: 'transparent', border: 'none',
                        fontSize: 12.5, lineHeight: 1.45, color: 'var(--ink-2)',
                        fontFamily: 'inherit', outline: 'none', resize: 'vertical',
                      }}
                    />
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 4 }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingId(null); setEditDraft(''); }}
                        style={{
                          padding: '4px 10px', borderRadius: 8,
                          background: 'transparent', border: '1px solid var(--line)',
                          fontSize: 11, color: 'var(--ink-3)', cursor: 'pointer',
                        }}
                      >Cancelar</button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEditSave(c.id); }}
                        disabled={!editDraft.trim()}
                        style={{
                          padding: '4px 12px', borderRadius: 8,
                          background: editDraft.trim() ? 'var(--ember)' : 'var(--paper-3)',
                          border: 'none',
                          fontSize: 11, color: editDraft.trim() ? 'var(--paper)' : 'var(--ink-4)',
                          cursor: editDraft.trim() ? 'pointer' : 'default', fontWeight: 500,
                        }}
                      >Salvar</button>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    padding: '8px 12px', borderRadius: 14, borderTopLeftRadius: 4,
                    background: 'var(--paper-2)',
                    position: 'relative',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 2 }}>{c.author?.name || 'Membro'}</div>
                      {canManage && (
                        <PostManageMenu
                          canEdit
                          onEdit={() => { setEditingId(c.id); setEditDraft(c.text || ''); }}
                          onDelete={() => handleDelete(c.id)}
                        />
                      )}
                    </div>
                    <div style={{ fontSize: 12.5, lineHeight: 1.45, color: 'var(--ink-2)', whiteSpace: 'pre-wrap' }}>
                      {c.text}
                      {c.editedAt && (
                        <span style={{ fontSize: 10, color: 'var(--ink-4)', fontStyle: 'italic', marginLeft: 6 }}>(editado)</span>
                      )}
                    </div>
                  </div>
                )}
                <div className="mono" style={{
                  fontSize: 9.5, color: 'var(--ink-4)',
                  letterSpacing: '0.06em', marginTop: 3, paddingLeft: 12,
                }}>{c.timeAgo}</div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Permissão de gerenciar post/comment ───
// Membros editam/excluem o próprio conteúdo; admin/owner/moderator podem
// editar/excluir qualquer um do grupo. Backend revalida — UI só esconde o
// botão quando claramente não pode.
const canManageItem = (authorId: string | undefined, viewerId: string | undefined, viewerIsModerator: boolean) =>
  !!viewerId && (authorId === viewerId || viewerIsModerator);

// ─── PostManageMenu: botão "..." que abre popover Editar/Excluir/Denunciar ───
const PostManageMenu: React.FC<{
  onEdit?: () => void;
  onDelete?: () => void;
  onReport?: () => void;
  canEdit?: boolean; // alguns tipos (quiz/question-box) só permitem excluir nesta versão
  canManage?: boolean; // se viewer é autor/mod (mostra Editar/Excluir)
}> = ({ onEdit, onDelete, onReport, canEdit = true, canManage = true }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(v => !v); }}
        aria-label="Opções do post"
        style={{
          width: 28, height: 28, borderRadius: '50%',
          background: open ? 'var(--paper-3)' : 'transparent',
          border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', transition: 'background 120ms',
        }}
      >
        <Icon name="more" size={16} color="var(--ink-3)"/>
      </button>
      {open && (
        <>
          {/* backdrop pra fechar ao clicar fora */}
          <div
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); }}
            style={{ position: 'fixed', inset: 0, zIndex: 30 }}
          />
          <div style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 4,
            background: 'var(--paper)', borderRadius: 12,
            border: '1px solid var(--line)',
            boxShadow: '0 8px 24px -8px rgba(0,0,0,0.18)',
            minWidth: 140, padding: 4, zIndex: 31,
            overflow: 'hidden',
          }}>
            {canManage && canEdit && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); onEdit?.(); }}
                style={{
                  width: '100%', textAlign: 'left',
                  padding: '10px 12px', borderRadius: 8,
                  background: 'transparent', border: 'none',
                  fontSize: 13, color: 'var(--ink)',
                  display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--paper-2)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <Icon name="edit" size={14} color="var(--ink-3)"/> Editar
              </button>
            )}
            {canManage && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); onDelete?.(); }}
                style={{
                  width: '100%', textAlign: 'left',
                  padding: '10px 12px', borderRadius: 8,
                  background: 'transparent', border: 'none',
                  fontSize: 13, color: '#C0392B',
                  display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(192,57,43,0.08)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <Icon name="trash" size={14} color="#C0392B"/> Excluir
              </button>
            )}
            {/* Denunciar — sempre disponivel se nao for o autor (UGC Apple G1.2) */}
            {!canManage && onReport && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); onReport(); }}
                style={{
                  width: '100%', textAlign: 'left',
                  padding: '10px 12px', borderRadius: 8,
                  background: 'transparent', border: 'none',
                  fontSize: 13, color: 'var(--ember)',
                  display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(199,83,26,0.08)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 21V4M4 4h12l-2 5 2 5H4"/>
                </svg>
                Denunciar
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ─── EditPostSheet: bottom sheet com textarea pra editar conteúdo do post ───
// Suporta os campos textuais por tipo. Quiz e question-box mostram aviso
// pedindo pra excluir e recriar (pra preservar votos/respostas existentes).
const EditPostSheet: React.FC<{
  post: any;
  onClose: () => void;
  onSave: (patch: any) => Promise<void>;
}> = ({ post, onClose, onSave }) => {
  const kind = post.kind;
  const [text, setText] = useState<string>(post.text || post.content?.text || '');
  const [title, setTitle] = useState<string>(post.title || post.content?.title || '');
  const [comment, setComment] = useState<string>(post.comment || post.content?.comment || '');
  const [question, setQuestion] = useState<string>(post.question || post.content?.question || '');
  const [saving, setSaving] = useState(false);

  const canEditTextually = ['reflection', 'citation', 'question', 'leader-answer', 'insight-final'].includes(kind);

  const buildPatch = () => {
    switch (kind) {
      case 'reflection':    return { text };
      case 'citation':      return { text, comment };
      case 'question':      return { text };
      case 'leader-answer': return { text };
      case 'insight-final': return { title, text };
      default: return null;
    }
  };

  const handleSave = async () => {
    const patch = buildPatch();
    if (!patch) return;
    setSaving(true);
    try {
      await onSave(patch);
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao editar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        zIndex: 90, animation: 'fadeIn 200ms',
      }}/>
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0,
        background: 'var(--paper)',
        borderTopLeftRadius: 24, borderTopRightRadius: 24,
        padding: '14px 18px 28px',
        zIndex: 91,
        maxWidth: 480, margin: '0 auto',
        animation: 'slideInUp 280ms cubic-bezier(0.22, 1, 0.36, 1)',
        maxHeight: '85vh', overflowY: 'auto',
      }}>
        <div style={{
          width: 40, height: 4, borderRadius: 2,
          background: 'var(--line-strong)', margin: '0 auto 12px',
        }}/>
        <div className="serif" style={{ fontSize: 22, lineHeight: 1.1, marginBottom: 16 }}>
          Editar <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>post</span>
        </div>

        {!canEditTextually && (
          <div style={{
            padding: 14, borderRadius: 12,
            background: 'var(--paper-2)', border: '1px dashed var(--line-strong)',
            fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 14,
          }}>
            Esse tipo de post (<b>{kind}</b>) tem interações registradas (votos/respostas).
            Edite excluindo e recriando — os dados de quem já participou serão preservados no banco mas não no novo post.
          </div>
        )}

        {kind === 'insight-final' && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Título (opcional)</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Título"
              style={{
                width: '100%', marginTop: 6,
                padding: '10px 14px', borderRadius: 12,
                background: 'var(--paper-2)', border: '1px solid var(--line)',
                fontSize: 14, outline: 'none', fontFamily: 'inherit', color: 'var(--ink)',
              }}
            />
          </div>
        )}

        {canEditTextually && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {kind === 'citation' ? 'Citação' : kind === 'question' ? 'Pergunta' : kind === 'leader-answer' ? 'Resposta' : 'Texto'}
            </label>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              rows={kind === 'reflection' || kind === 'insight-final' || kind === 'leader-answer' ? 8 : 5}
              placeholder="Escreva..."
              style={{
                width: '100%', marginTop: 6,
                padding: '12px 14px', borderRadius: 12,
                background: 'var(--paper-2)', border: '1px solid var(--line)',
                fontSize: 14, lineHeight: 1.5, outline: 'none', fontFamily: 'inherit', color: 'var(--ink)',
                resize: 'vertical',
              }}
            />
          </div>
        )}

        {kind === 'citation' && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Comentário (opcional)</label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              rows={3}
              placeholder="Por que essa citação te marcou?"
              style={{
                width: '100%', marginTop: 6,
                padding: '12px 14px', borderRadius: 12,
                background: 'var(--paper-2)', border: '1px solid var(--line)',
                fontSize: 14, lineHeight: 1.5, outline: 'none', fontFamily: 'inherit', color: 'var(--ink)',
                resize: 'vertical',
              }}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} disabled={saving} style={{
            flex: 1, padding: '12px 16px', borderRadius: 12,
            background: 'var(--paper-2)', border: '1px solid var(--line)',
            fontSize: 14, color: 'var(--ink-2)', cursor: 'pointer',
          }}>Cancelar</button>
          <button
            onClick={handleSave}
            disabled={saving || !canEditTextually || !text.trim()}
            style={{
              flex: 1, padding: '12px 16px', borderRadius: 12,
              background: saving || !canEditTextually || !text.trim() ? 'var(--paper-3)' : 'var(--ember)',
              border: 'none',
              fontSize: 14, color: saving || !canEditTextually || !text.trim() ? 'var(--ink-4)' : 'var(--paper)',
              cursor: saving || !canEditTextually || !text.trim() ? 'default' : 'pointer',
              fontWeight: 500,
            }}
          >{saving ? 'Salvando…' : 'Salvar'}</button>
        </div>
      </div>
    </>
  );
};

// ─── Variantes de post ───
// Cada PostCard recebe clubId/session/canPost pra renderizar o CommentsBlock
// interativo (lista + input de comentário). onCommentAdded propaga pro parent
// pra que a contagem em `posts` state fique sincronizada após criar comentário.
//
// Para edit/delete: viewerId + viewerIsModerator decidem se o menu "..." aparece.
// onEditPost/onDeletePost são handlers que sobem até o App.tsx (chamada API +
// atualização do posts state). PostManageMenu é renderizado inline no header
// de cada PostCard quando canManage.
type PostCardCommonProps = {
  clubId?: string;
  session?: any;
  canPost?: boolean;
  onCommentAdded?: (postId: string, delta: number) => void;
  viewerId?: string;
  viewerIsModerator?: boolean;
  onEditPost?: (postId: string, patch: any) => Promise<void>;
  onDeletePost?: (postId: string) => Promise<void>;
  /** Denuncia um post — UGC Apple Guideline 1.2. */
  onReportPost?: (post: any) => void;
  // Quando user clica "Compartilhar como você leu" num question, sobe a pergunta
  // pra App.tsx que abre o ComposeSheet em modo reply.
  onReplyToQuestion?: (post: any) => void;
  // Quando admin clica "Ver perguntas" num question-box, App navega pra tela admin
  onOpenQboxAdmin?: (postId: string) => void;
};

// Helper de uso comum em cada PostCard: renderiza menu "..." + sheet de edição
// se o viewer tem permissão. Retorna { menu, sheet } pra inserir nos pontos certos
// do card. Mantém o estado do sheet local ao card.
const usePostManage = (
  post: any,
  common: PostCardCommonProps,
) => {
  const [editing, setEditing] = useState(false);
  const canManage = canManageItem(post.author?.id, common.viewerId, !!common.viewerIsModerator);
  const canEditTextually = ['reflection', 'citation', 'question', 'leader-answer', 'insight-final'].includes(post.kind);

  // Menu aparece SEMPRE — pro autor mostra Editar/Excluir, pros demais
  // mostra Denunciar (Apple Guideline 1.2 — UGC moderation).
  const showMenu = canManage || !!common.onReportPost;
  const menu = showMenu ? (
    <PostManageMenu
      canManage={canManage}
      canEdit={canEditTextually}
      onEdit={() => setEditing(true)}
      onDelete={async () => {
        if (!window.confirm('Excluir este post? Esta ação não pode ser desfeita.')) return;
        try { await common.onDeletePost?.(post.id); }
        catch (e: any) { toast.error(e?.message || 'Erro ao excluir'); }
      }}
      onReport={() => common.onReportPost?.(post)}
    />
  ) : null;

  const sheet = editing ? (
    <EditPostSheet
      post={post}
      onClose={() => setEditing(false)}
      onSave={(patch) => common.onEditPost!(post.id, patch)}
    />
  ) : null;

  return { menu, sheet };
};

const ReflectionPost: React.FC<{ post: any; onReact?: any } & PostCardCommonProps> = (props) => {
  const { post, onReact, clubId, session, canPost, onCommentAdded } = props;
  const { menu, sheet } = usePostManage(post, props);
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
        <TypeBadge kind="reflection"/>
        {menu}
      </div>
      <CompactAuthorRow author={post.author} page={post.page} timeAgo={post.timeAgo} authorBook={post.authorBook}/>
      <div className="serif" style={{
        fontSize: 16, lineHeight: 1.45, marginTop: 12,
        color: 'var(--ink)', textWrap: 'pretty',
      }}>
        {post.text && post.text.length > 280 ? <>{post.text.slice(0, 280)}… <button style={{ color: 'var(--ember)', fontStyle: 'italic' }}>ver mais</button></> : post.text}
        {(post.editedAt || post.content?.editedAt) && (
          <span style={{ fontSize: 11, color: 'var(--ink-4)', fontStyle: 'italic', marginLeft: 6 }}>(editado)</span>
        )}
      </div>
      <PostFooter post={post} onReact={onReact} clubId={clubId} session={session} canPost={canPost}
        viewerId={props.viewerId} viewerIsModerator={props.viewerIsModerator}
        onCommentAdded={onCommentAdded}/>
      {sheet}
    </div>
  );
};

const CitationPost: React.FC<{ post: any; onReact?: any } & PostCardCommonProps> = (props) => {
  const { post, onReact, clubId, session, canPost, onCommentAdded } = props;
  const { menu, sheet } = usePostManage(post, props);
  return (
  <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
    <div style={{ padding: '16px 16px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
      <TypeBadge kind="citation"/>
      {menu}
    </div>
    <div style={{ padding: '0 16px' }}>
      <CompactAuthorRow author={post.author} page={post.page} timeAgo={post.timeAgo} authorBook={post.authorBook}/>
    </div>
    <div style={{
      margin: '14px 16px',
      padding: '20px 22px',
      background: 'var(--citation-bg)',
      borderRadius: 16, position: 'relative',
    }}>
      <div style={{
        position: 'absolute', top: 6, left: 14,
        fontFamily: 'Instrument Serif, serif', fontStyle: 'italic',
        fontSize: 50, lineHeight: 1, color: 'var(--citation-quote)',
      }}>"</div>
      <div className="serif" style={{
        fontSize: 19, lineHeight: 1.4, fontStyle: 'italic',
        color: 'var(--ink)', textWrap: 'pretty', paddingLeft: 4,
      }}>
        {post.text}
        {(post.editedAt || post.content?.editedAt) && (
          <span style={{ fontSize: 11, color: 'var(--ink-4)', fontStyle: 'normal', marginLeft: 6 }}>(editado)</span>
        )}
      </div>
    </div>
    {post.comment && (
      <div style={{ padding: '0 16px 12px', fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>
        {post.comment}
      </div>
    )}
    <div style={{ padding: '0 16px 14px' }}>
      <PostFooter post={post} onReact={onReact} clubId={clubId} session={session} canPost={canPost}
        viewerId={props.viewerId} viewerIsModerator={props.viewerIsModerator}
        onCommentAdded={onCommentAdded} marginTop={4}/>
    </div>
    {sheet}
  </div>
  );
};

const QuestionPost: React.FC<{ post: any; onReact?: any } & PostCardCommonProps> = (props) => {
  const { post, onReact, canPost, onReplyToQuestion, clubId, session, onCommentAdded } = props;
  const { menu, sheet } = usePostManage(post, props);
  const respCount = post.responses ?? post.content?.responses ?? 0;
  const handleReply = () => {
    if (!canPost) return;
    onReplyToQuestion?.(post);
  };
  return (
  <div className="card" style={{ padding: 16 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, justifyContent: 'space-between' }}>
      <TypeBadge kind="question"/>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span className="mono" style={{ fontSize: 9.5, letterSpacing: '0.08em', color: 'var(--ink-3)' }}>
          {respCount} {respCount === 1 ? 'resposta' : 'respostas'}{respCount === 0 ? ' · aguardando' : ''}
        </span>
        {menu}
      </div>
    </div>
    <CompactAuthorRow author={post.author} page={post.page} timeAgo={post.timeAgo} authorBook={post.authorBook}/>
    <div className="serif" style={{
      fontSize: 17, lineHeight: 1.4, marginTop: 10,
      color: 'var(--ink)', textWrap: 'pretty', fontStyle: 'italic',
    }}>
      {post.text}
      {(post.editedAt || post.content?.editedAt) && (
        <span style={{ fontSize: 11, color: 'var(--ink-4)', fontStyle: 'normal', marginLeft: 6 }}>(editado)</span>
      )}
    </div>
    <button onClick={handleReply} disabled={!canPost} style={{
      marginTop: 14, width: '100%',
      padding: '12px 14px', borderRadius: 12,
      background: canPost ? 'var(--paper-3)' : 'var(--paper-2)',
      color: canPost ? 'var(--ink-2)' : 'var(--ink-4)',
      fontSize: 13, fontStyle: 'italic',
      border: '1px solid var(--line)',
      textAlign: 'left',
      display: 'flex', alignItems: 'center', gap: 10,
      cursor: canPost ? 'pointer' : 'default',
    }}>
      <Icon name="pen" size={14} color={canPost ? 'var(--ink-3)' : 'var(--ink-4)'}/>
      {canPost ? 'Compartilhar como você leu...' : 'Entre no grupo pra responder...'}
    </button>
    <PostFooter post={post} onReact={onReact} clubId={clubId} session={session} canPost={canPost}
      viewerId={props.viewerId} viewerIsModerator={props.viewerIsModerator}
      onCommentAdded={onCommentAdded}/>
    {sheet}
  </div>
  );
};

// QuizPost multi-pergunta: navega pergunta por pergunta, mostra resultado de cada
// uma após resposta + tela final consolidada (X de N acertos quando há gabarito).
//
// Estado:
//   currentIdx: pergunta atual (0..N-1; N = "résultat final")
//   answers: { [questionId]: optionId } — sincroniza com post.yourAnswers do backend
//   localQuestions: override de questions[] após cada voto (counts atualizados)
//
// Fluxo:
//   - Ao montar, currentIdx = primeira pergunta NÃO respondida (ou N se todas respondidas)
//   - User clica opção → API call → atualiza counts daquela pergunta → mostra resultado
//   - Botão "Próxima" → currentIdx++ (ou "Ver resultado final" no último)
//   - Botão "Refazer essa" sai do modo result e volta opções (se quiser trocar voto)
const QuizPost: React.FC<{ post: any } & PostCardCommonProps> = (props) => {
  const { post, clubId, session, canPost } = props;
  const { menu, sheet } = usePostManage(post, props);

  // Normaliza: backend já retorna content.questions[] (mesmo pra single legado)
  const baseQuestions: any[] = post.content?.questions || post.questions || [];
  const hasGabarito = !!post.hasGabarito;
  const totalQ = baseQuestions.length;

  // Respostas do viewer (vindas do backend) + override local após votar
  const [answers, setAnswers] = useState<Record<string, string>>(post.yourAnswers || {});
  const [localQuestions, setLocalQuestions] = useState<any[] | null>(null);
  const questions = localQuestions || baseQuestions;

  // Pergunta atual: começa na primeira não-respondida (ou no final = result screen)
  const initialIdx = (() => {
    if (!post.yourAnswers) return 0;
    for (let i = 0; i < baseQuestions.length; i++) {
      if (!post.yourAnswers[baseQuestions[i].id]) return i;
    }
    return baseQuestions.length; // todas respondidas → resultado
  })();
  const [currentIdx, setCurrentIdx] = useState(initialIdx);
  const [voting, setVoting] = useState(false);

  // Resync se o post mudar (filter change, refresh)
  useEffect(() => {
    setAnswers(post.yourAnswers || {});
    setLocalQuestions(null);
    let idx = 0;
    if (post.yourAnswers) {
      for (let i = 0; i < baseQuestions.length; i++) {
        if (!post.yourAnswers[baseQuestions[i].id]) { idx = i; break; }
        idx = i + 1;
      }
    }
    setCurrentIdx(idx);
  }, [post.id, JSON.stringify(post.yourAnswers || {}), totalQ]); // eslint-disable-line

  const handleVote = async (questionId: string, optionId: string) => {
    if (voting || !session || !clubId || !canPost) return;
    setVoting(true);
    try {
      const r = await api.voteQuiz(session, clubId, post.id, questionId, optionId);
      // Atualiza answers + counts dessa pergunta no override local
      setAnswers(prev => ({ ...prev, [questionId]: r.yourVote }));
      setLocalQuestions(prev => {
        const src = prev || baseQuestions;
        return src.map(q => q.id === questionId
          ? { ...q, options: r.options, totalVotes: r.totalVotes }
          : q);
      });
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao votar');
    } finally {
      setVoting(false);
    }
  };

  // Conta acertos quando há gabarito
  const correctCount = hasGabarito ? questions.reduce((acc, q) => {
    const ans = answers[q.id];
    if (!ans) return acc;
    const correct = (q.options || []).find((o: any) => o.correct);
    return acc + (correct && correct.id === ans ? 1 : 0);
  }, 0) : 0;
  const answeredCount = Object.keys(answers).filter(qid =>
    questions.some(q => q.id === qid)
  ).length;

  const isFinalScreen = currentIdx >= totalQ;
  const currentQ = !isFinalScreen ? questions[currentIdx] : null;
  const currentAnswer = currentQ ? answers[currentQ.id] : null;
  const currentReveal = !!currentAnswer && hasGabarito;

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <TypeBadge kind="quiz"/>
          <span className="mono" style={{ fontSize: 9.5, letterSpacing: '0.08em', color: 'var(--ink-3)' }}>
            {hasGabarito ? 'com gabarito' : 'enquete'}{totalQ > 1 ? ` · ${totalQ} perguntas` : ''}
          </span>
        </div>
        {menu}
      </div>
      <CompactAuthorRow author={post.author} page={post.page} timeAgo={post.timeAgo} authorBook={post.authorBook}/>

      {/* Título do quiz (multi-pergunta) — quando vier do compose */}
      {post.content?.title && (
        <div className="serif" style={{
          fontSize: 20, lineHeight: 1.2, marginTop: 12,
          color: 'var(--ink)', fontStyle: 'italic',
        }}>{post.content.title}</div>
      )}

      {/* Progresso multi-pergunta */}
      {totalQ > 1 && (
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          {questions.map((q: any, i: number) => {
            const done = !!answers[q.id];
            const isCurrent = i === currentIdx;
            return (
              <div key={q.id} style={{
                flex: 1, height: 4, borderRadius: 2,
                background: done ? 'var(--ember)' : isCurrent ? 'var(--ink-3)' : 'var(--paper-3)',
                transition: 'background 200ms',
              }}/>
            );
          })}
        </div>
      )}

      {/* TELA FINAL: resultado consolidado */}
      {isFinalScreen ? (
        <div style={{
          marginTop: 14, padding: '20px 18px', borderRadius: 14,
          background: 'var(--paper-2)',
          border: '1px solid var(--line)',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 36, marginBottom: 6 }}>
            {hasGabarito ? (correctCount === totalQ ? '🎯' : correctCount >= totalQ / 2 ? '👏' : '📚') : '✓'}
          </div>
          <div className="serif" style={{ fontSize: 22, lineHeight: 1.1, marginBottom: 6 }}>
            {hasGabarito
              ? <>Você acertou <span style={{ color: 'var(--ember)', fontStyle: 'italic' }}>{correctCount} de {totalQ}</span></>
              : <>Você respondeu <span style={{ color: 'var(--ember)', fontStyle: 'italic' }}>{answeredCount} {answeredCount === 1 ? 'pergunta' : 'perguntas'}</span></>
            }
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.45 }}>
            {hasGabarito
              ? (correctCount === totalQ ? 'Mandou bem! Quiz completo.' : 'Quiz finalizado. Revisita as respostas pra estudar.')
              : 'Quiz finalizado. Suas respostas ajudam o líder a entender o grupo.'}
          </div>
          <button
            onClick={() => setCurrentIdx(0)}
            style={{
              marginTop: 14, padding: '8px 14px', borderRadius: 999,
              background: 'transparent', border: '1px solid var(--line-strong)',
              fontSize: 12, color: 'var(--ink-2)', cursor: 'pointer',
            }}
          >Revisitar respostas</button>
        </div>
      ) : (
      <>
      {/* Enunciado da pergunta atual */}
      <div className="serif" style={{
        fontSize: 18, lineHeight: 1.3, marginTop: 14, color: 'var(--ink)', textWrap: 'pretty',
      }}>
        {totalQ > 1 && (
          <span className="mono" style={{
            fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase',
            color: 'var(--ember)', fontWeight: 600, marginRight: 8,
          }}>
            Pergunta {currentIdx + 1}/{totalQ}
          </span>
        )}
        {currentQ.text}
        {(post.editedAt || post.content?.editedAt) && (
          <span style={{ fontSize: 11, color: 'var(--ink-4)', fontStyle: 'italic', marginLeft: 6, fontWeight: 400 }}>(editado)</span>
        )}
      </div>

      {/* Opções */}
      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(currentQ.options || []).map((opt: any) => {
          const optVotes = opt.votes || 0;
          const totalThis = currentQ.totalVotes || 0;
          const pct = currentAnswer && totalThis > 0 ? Math.round((optVotes / totalThis) * 100) : 0;
          const isPicked = currentAnswer === opt.id;
          const isCorrect = opt.correct;
          return (
            <button
              key={opt.id}
              onClick={() => handleVote(currentQ.id, opt.id)}
              disabled={!!currentAnswer || voting || !canPost}
              style={{
                width: '100%', position: 'relative',
                padding: '14px 16px', borderRadius: 14,
                background: 'var(--paper)',
                border: `1.5px solid ${
                  currentReveal && isCorrect ? 'var(--sage)' :
                  currentReveal && isPicked && !isCorrect ? 'var(--ember)' :
                  isPicked ? 'var(--ink)' : 'var(--line-strong)'
                }`,
                textAlign: 'left', overflow: 'hidden', transition: 'all 200ms',
                cursor: currentAnswer || !canPost ? 'default' : 'pointer',
                opacity: !canPost && !currentAnswer ? 0.7 : 1,
              }}>
              {currentAnswer && (
                <div style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: `${pct}%`,
                  background: currentReveal && isCorrect ? 'var(--quiz-correct-bar)' :
                             currentReveal && isPicked && !isCorrect ? 'var(--quiz-wrong-bar)' :
                             'var(--paper-2)',
                  transition: 'width 600ms cubic-bezier(0.22, 1, 0.36, 1)', zIndex: 0,
                }}/>
              )}
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 22, height: 22, borderRadius: '50%',
                  border: `1.5px solid ${
                    currentReveal && isCorrect ? 'var(--sage)' :
                    currentReveal && isPicked && !isCorrect ? 'var(--ember)' :
                    isPicked ? 'var(--ink)' : 'var(--line-strong)'
                  }`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  background: currentReveal && isCorrect ? 'var(--sage)' :
                              isPicked ? 'var(--ink)' : 'transparent',
                }}>
                  {(isPicked || (currentReveal && isCorrect)) && (
                    <Icon name="check" size={12} color="var(--paper)" stroke={3}/>
                  )}
                </div>
                <span style={{ flex: 1, fontSize: 14, color: 'var(--ink)' }}>{opt.text}</span>
                {currentAnswer && (
                  <span className="mono" style={{
                    fontSize: 11, fontWeight: 600,
                    color: currentReveal && isCorrect ? 'var(--sage)' : 'var(--ink-2)',
                  }}>{pct}%</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Feedback de gabarito após responder */}
      {currentAnswer && hasGabarito && (() => {
        const correct = (currentQ.options || []).find((o: any) => o.correct);
        if (!correct) return null;
        const hit = currentAnswer === correct.id;
        const totalThis = currentQ.totalVotes || 0;
        return (
          <div style={{
            marginTop: 12, padding: '10px 14px', borderRadius: 12,
            background: hit ? 'var(--quiz-correct-bg)' : 'var(--quiz-wrong-bg)',
            border: `1px solid ${hit ? 'var(--quiz-correct-border)' : 'var(--quiz-wrong-border)'}`,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: 16 }}>{hit ? '✓' : '✕'}</span>
            <div className="serif" style={{ fontSize: 13, fontStyle: 'italic', color: 'var(--ink)' }}>
              {hit ? 'Você acertou.' : `Resposta correta: "${correct.text}".`}
              {totalThis > 0 && (
                <span style={{
                  color: 'var(--ink-3)', fontStyle: 'normal',
                  fontFamily: 'Geist Mono, monospace', fontSize: 10, marginLeft: 6,
                }}>
                  · {Math.round(((correct.votes || 0) / totalThis) * 100)}% acertou
                </span>
              )}
            </div>
          </div>
        );
      })()}

      {/* Navegação */}
      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.06em' }}>
          {currentQ.totalVotes || 0} {(currentQ.totalVotes || 0) === 1 ? 'resposta' : 'respostas'}
          {!canPost && !currentAnswer && ' · entre no grupo pra votar'}
        </span>
        {currentAnswer && (
          <div style={{ display: 'flex', gap: 6 }}>
            {currentIdx > 0 && (
              <button
                onClick={() => setCurrentIdx(currentIdx - 1)}
                style={{
                  padding: '8px 12px', borderRadius: 999,
                  background: 'transparent', border: '1px solid var(--line-strong)',
                  fontSize: 12, color: 'var(--ink-2)', cursor: 'pointer',
                }}
              >← anterior</button>
            )}
            <button
              onClick={() => setCurrentIdx(currentIdx + 1)}
              style={{
                padding: '8px 14px', borderRadius: 999,
                background: 'var(--ember)', border: 'none',
                fontSize: 12, color: 'var(--paper)', fontWeight: 500, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              {currentIdx === totalQ - 1 ? 'Ver resultado final' : 'Próxima →'}
            </button>
          </div>
        )}
      </div>
      </>
      )}
      {sheet}
    </div>
  );
};

// QuestionBoxPost: card no feed onde membros enviam perguntas anônimas + admin
// abre tela full-screen pra responder. Admin (viewerIsModerator) vê botão extra
// "Ver perguntas" que leva pra ClubQuestionBoxAdminScreen.
const QuestionBoxPost: React.FC<{ post: any; onOpenAdmin?: (postId: string) => void } & PostCardCommonProps> = (props) => {
  const { post, clubId, session, canPost, onOpenAdmin } = props;
  const { menu, sheet } = usePostManage(post, props);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  // Count atualizado localmente após envio bem-sucedido (otimismo)
  const [localCount, setLocalCount] = useState<number | null>(null);
  const count = localCount !== null ? localCount : (post.questionsReceived ?? post.content?.questionsReceived ?? 0);

  const handleSend = async () => {
    if (!draft.trim() || !session || !clubId || sending || !canPost) return;
    setSending(true);
    try {
      await api.askQuestionBox(session, clubId, post.id, draft.trim());
      setSent(true);
      setLocalCount(c => (c ?? 0) + 1);
      setDraft('');
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao enviar pergunta');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{
      borderRadius: 22, padding: 16,
      background: 'var(--qbox-bg)',
      border: '1px solid var(--qbox-border)',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, justifyContent: 'space-between' }}>
        <TypeBadge kind="question-box"/>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="mono" style={{ fontSize: 9.5, letterSpacing: '0.08em', color: 'var(--qbox-text-2)' }}>
            {post.closesIn ? `fecha em ${post.closesIn}` : 'aberta'}
          </span>
          {menu}
        </div>
      </div>
      <CompactAuthorRow author={post.author} timeAgo={post.timeAgo} authorBook={post.authorBook}/>
      <div className="serif" style={{
        fontSize: 22, lineHeight: 1.15, marginTop: 12,
        color: 'var(--qbox-text)', fontStyle: 'italic',
      }}>{post.prompt}</div>
      <div style={{ fontSize: 11.5, color: 'var(--qbox-text-2)', marginTop: 6 }}>
        Sua identidade não aparece publicamente. O líder vê para responder.
      </div>
      {!sent ? (
        <div style={{ marginTop: 14 }}>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value.slice(0, 1000))}
            placeholder={canPost ? 'Escreva sua pergunta...' : 'Entre no grupo pra enviar uma pergunta'}
            disabled={!canPost || sending}
            style={{
              width: '100%', minHeight: 70, padding: '12px 14px', borderRadius: 12,
              background: 'var(--paper-3)',
              border: '1px solid var(--line)',
              fontSize: 14, lineHeight: 1.4, resize: 'none',
              outline: 'none', color: 'var(--ink)', fontFamily: 'inherit',
              transition: 'border-color 160ms, background 160ms',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--ember)';
              e.currentTarget.style.background = 'var(--paper-2)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'var(--line)';
              e.currentTarget.style.background = 'var(--paper-3)';
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <button
              onClick={handleSend}
              disabled={!draft.trim() || !canPost || sending}
              style={{
                padding: '10px 16px', borderRadius: 12,
                background: draft.trim() && canPost && !sending ? 'var(--qbox-cta)' : 'var(--qbox-cta-disabled)',
                color: 'white', fontSize: 13, fontWeight: 500, border: 'none',
                opacity: draft.trim() && canPost && !sending ? 1 : 0.6,
                cursor: draft.trim() && canPost && !sending ? 'pointer' : 'default',
              }}
            >{sending ? 'Enviando…' : 'Enviar pergunta'}</button>
            {props.viewerIsModerator && onOpenAdmin && (
              <button
                onClick={() => onOpenAdmin(post.id)}
                style={{
                  padding: '8px 12px', borderRadius: 999,
                  background: 'transparent', border: '1px solid var(--qbox-border)',
                  fontSize: 12, color: 'var(--qbox-text-2)', cursor: 'pointer',
                }}
              >Ver perguntas →</button>
            )}
          </div>
        </div>
      ) : (
        <div style={{
          marginTop: 14, padding: '14px 16px', borderRadius: 12,
          background: 'var(--qbox-input-bg)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Icon name="check" size={16} color="var(--qbox-text)"/>
          <div className="serif" style={{ flex: 1, fontSize: 14, fontStyle: 'italic', color: 'var(--qbox-text)' }}>
            Pergunta enviada. Você verá quando o líder responder.
          </div>
          <button
            onClick={() => setSent(false)}
            style={{
              padding: '6px 10px', borderRadius: 999,
              background: 'transparent', border: '1px solid var(--qbox-border)',
              fontSize: 11, color: 'var(--qbox-text-2)', cursor: 'pointer',
            }}
          >+ outra</button>
        </div>
      )}
      <div className="mono" style={{ fontSize: 9.5, color: 'var(--qbox-text-3)', marginTop: 12, letterSpacing: '0.08em' }}>
        {count} {count === 1 ? 'pergunta recebida' : 'perguntas recebidas'}
      </div>
      {sheet}
    </div>
  );
};

const LeaderAnswerPost: React.FC<{ post: any; onReact?: any } & PostCardCommonProps> = (props) => {
  const { post, onReact, clubId, session, canPost, onCommentAdded } = props;
  const { menu, sheet } = usePostManage(post, props);
  return (
  <div className="card" style={{
    padding: 0, overflow: 'hidden',
    background: 'var(--paper-2)',
    border: '1px solid color-mix(in oklab, var(--ember) 28%, transparent)',
  }}>
    {/* Header: badge + menu */}
    <div style={{
      padding: '12px 14px 0',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
    }}>
      <TypeBadge kind="leader-answer"/>
      {menu}
    </div>

    {/* Bloco da pergunta — centralizado e com margens generosas pra "respirar"
        dentro do card. Border ember sutil ao redor + bg tingida ember dão o
        sinal visual sem precisar de barra lateral. Texto centrado focaliza
        a leitura na pergunta antes da resposta. */}
    <div style={{
      margin: '14px 26px 0',
      padding: '12px 16px 13px',
      background: 'color-mix(in oklab, var(--ember) 9%, var(--paper-3))',
      borderRadius: 12,
      border: '1px solid color-mix(in oklab, var(--ember) 22%, transparent)',
      textAlign: 'center',
    }}>
      <div className="mono" style={{
        fontSize: 8.5, letterSpacing: '0.18em', textTransform: 'uppercase',
        color: 'var(--ember)', fontWeight: 600, opacity: 0.9,
      }}>alguém perguntou</div>
      <div style={{
        fontSize: 13, lineHeight: 1.4, fontStyle: 'italic',
        color: 'var(--ink-2)', marginTop: 4, textWrap: 'pretty',
      }}>"{post.question}"</div>
    </div>

    {/* Corpo: autor compacto + texto da resposta + toolbar */}
    <div style={{ padding: '12px 14px 14px' }}>
      <CompactAuthorRow author={post.author} timeAgo={post.timeAgo} authorBook={post.authorBook}/>
      <div style={{
        fontSize: 14.5, lineHeight: 1.55, marginTop: 10,
        color: 'var(--ink)', textWrap: 'pretty',
      }}>
        {post.text}
        {(post.editedAt || post.content?.editedAt) && (
          <span style={{ fontSize: 11, color: 'var(--ink-4)', fontStyle: 'italic', marginLeft: 6 }}>(editado)</span>
        )}
      </div>

      <PostFooter post={post} onReact={onReact} clubId={clubId} session={session} canPost={canPost}
        viewerId={props.viewerId} viewerIsModerator={props.viewerIsModerator}
        onCommentAdded={onCommentAdded}/>
    </div>
    {sheet}
  </div>
  );
};

const InsightFinalPostCard: React.FC<{
  post: any; onOpen?: () => void; compact?: boolean; onReact?: any
} & PostCardCommonProps> = (props) => {
  const { post, onOpen, compact = false, onReact, clubId, session, canPost, onCommentAdded } = props;
  const { menu, sheet } = usePostManage(post, props);
  return (
  <div onClick={onOpen} style={{
    padding: compact ? 14 : 18,
    borderRadius: compact ? 18 : 22,
    background: 'var(--insight-bg)',
    border: `${compact ? 1.5 : 2}px solid color-mix(in oklab, var(--ember) ${compact ? 25 : 40}%, transparent)`,
    position: 'relative', overflow: 'hidden',
    cursor: onOpen ? 'pointer' : 'default',
    boxShadow: compact ? 'none' : '0 14px 28px -16px rgba(199,83,26,0.35)',
  }}>
    {!compact && <div style={{
      position: 'absolute', top: -40, right: -40,
      width: 140, height: 140, borderRadius: '50%',
      border: '1px solid var(--ember)', opacity: 0.18,
    }}/>}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
      <TypeBadge kind="insight-final" size={compact ? 'sm' : 'md'}/>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span className="mono" style={{
          fontSize: compact ? 9 : 10, letterSpacing: '0.12em', textTransform: 'uppercase',
          color: 'var(--ember)', fontWeight: 600,
        }}>
          ✦ {post.milestoneLabel || `pág ${post.page}`}
        </span>
        {menu}
      </div>
    </div>
    <CompactAuthorRow author={post.author} timeAgo={post.timeAgo} authorBook={post.authorBook}/>
    {post.title && (
      <div className="serif" style={{
        fontSize: compact ? 18 : 22, lineHeight: 1.1,
        marginTop: 12, color: 'var(--ink)', letterSpacing: '-0.005em',
      }}>{post.title}</div>
    )}
    <div className="serif" style={{
      fontSize: compact ? 14.5 : 16, lineHeight: 1.5,
      marginTop: post.title ? 8 : 12,
      color: 'var(--ink-2)', textWrap: 'pretty',
    }}>
      {compact && post.text && post.text.length > 180 ? <>{post.text.slice(0, 180)}…</> : post.text}
      {(post.editedAt || post.content?.editedAt) && (
        <span style={{ fontSize: 11, color: 'var(--ink-4)', fontStyle: 'italic', marginLeft: 6 }}>(editado)</span>
      )}
    </div>
    <PostFooter post={post} onReact={onReact} clubId={clubId} session={session} canPost={canPost}
      viewerId={props.viewerId} viewerIsModerator={props.viewerIsModerator}
      onCommentAdded={onCommentAdded} marginTop={14}/>
    {sheet}
  </div>
  );
};

// Exportado: o Estudo em Caderno usa estes cartões para os tipos que ele não
// desenha sozinho (quiz e caixa de perguntas).
export const StudyPost: React.FC<{
  post: any;
  onOpen?: (insight: any) => void;
  onReact?: (postId: string, kind: 'tocou' | 'aprendi' | 'fogo' | 'salvar') => Promise<void>;
} & PostCardCommonProps> = ({
  post, onOpen, onReact, clubId, session, canPost, onCommentAdded,
  viewerId, viewerIsModerator, onEditPost, onDeletePost, onReportPost, onReplyToQuestion, onOpenQboxAdmin,
}) => {
  const common = { clubId, session, canPost, onCommentAdded, viewerId, viewerIsModerator, onEditPost, onDeletePost, onReportPost, onReplyToQuestion, onOpenQboxAdmin };
  switch (post.kind) {
    case 'reflection':    return <ReflectionPost post={post} onReact={onReact} {...common}/>;
    case 'citation':      return <CitationPost post={post} onReact={onReact} {...common}/>;
    case 'question':      return <QuestionPost post={post} onReact={onReact} {...common}/>;
    case 'quiz':          return <QuizPost post={post} {...common}/>;
    case 'question-box':  return <QuestionBoxPost post={post} onOpenAdmin={onOpenQboxAdmin} {...common}/>;
    case 'leader-answer': return <LeaderAnswerPost post={post} onReact={onReact} {...common}/>;
    case 'insight-final': return <InsightFinalPostCard post={post} onReact={onReact} onOpen={() => onOpen && onOpen(post)} {...common}/>;
    default: return null;
  }
};

const ActiveMilestoneHeader: React.FC<{ milestone: Milestone; yourPage?: number }> = ({ milestone: m, yourPage = 0 }) => {
  const targetPage = m.targetPage || 0;
  const remaining = Math.max(0, targetPage - yourPage);
  const reached = yourPage >= targetPage && targetPage > 0;
  return (
    <div style={{
      padding: 18, borderRadius: 22,
      background: 'linear-gradient(155deg, #1A1614 0%, #2A2320 100%)',
      color: '#F3ECDE',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: -30, right: -30,
        width: 120, height: 120, borderRadius: '50%',
        background: 'radial-gradient(circle, var(--ember) 0%, transparent 70%)',
        opacity: 0.4, filter: 'blur(20px)',
      }}/>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
        <span className="mono" style={{
          padding: '3px 8px', borderRadius: 999,
          background: 'var(--ember)', color: '#F3ECDE',
          fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700,
        }}>✦ Marco {m.n} ativo</span>
        {m.deadline && (
          <span className="mono" style={{
            fontSize: 10, letterSpacing: '0.08em', color: 'rgba(243,236,222,0.75)',
          }}>até {m.deadline}</span>
        )}
      </div>
      <div className="serif" style={{
        fontSize: 30, lineHeight: 1.05, marginTop: 12, letterSpacing: '-0.01em',
        position: 'relative', color: '#F3ECDE',
      }}>{m.label}</div>
      {m.chapterRef && (
        <div className="mono" style={{
          fontSize: 10.5, color: 'rgba(243,236,222,0.65)',
          letterSpacing: '0.08em', marginTop: 4, position: 'relative',
        }}>{m.chapterRef}</div>
      )}
      <div style={{ marginTop: 18, position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: 'rgba(243,236,222,0.85)' }}>
            Você está na página <strong style={{ color: 'var(--ember-2)' }}>{yourPage}</strong>
          </span>
          <span className="mono" style={{ fontSize: 10, color: 'rgba(243,236,222,0.6)', letterSpacing: '0.06em' }}>
            {reached ? '✓ marco alcançado' : `faltam ${remaining}`}
          </span>
        </div>
        <div style={{ height: 4, background: 'rgba(243,236,222,0.12)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{
            width: `${Math.min(100, (yourPage / (targetPage || 1)) * 100)}%`,
            height: '100%', background: 'var(--ember)',
            boxShadow: '0 0 10px var(--ember)',
          }}/>
        </div>
      </div>
    </div>
  );
};

const ClosedMilestone: React.FC<{
  milestone: Milestone;
  expanded: boolean;
  onToggle: () => void;
  onOpenInsight?: (i: MilestoneInsightFinal) => void;
  // Props pra carregar feed quando expandir
  session?: any;
  clubId?: string;
  onReact?: (postId: string, kind: 'tocou' | 'aprendi' | 'fogo' | 'salvar') => Promise<void>;
  canPost?: boolean;
  onCommentAdded?: (postId: string, delta: number) => void;
  viewerId?: string;
  viewerIsModerator?: boolean;
  onEditPost?: (postId: string, patch: any) => Promise<void>;
  onDeletePost?: (postId: string) => Promise<void>;
  onReplyToQuestion?: (post: any) => void;
  onOpenQboxAdmin?: (postId: string) => void;
  // Quando há marco ativo com posts novos, o marco anterior atenua visualmente
  // pra puxar o foco pro presente. Tap em qualquer item do marco anterior
  // restaura opacidade (hover/foco implícito via transition).
  dimmed?: boolean;
}> = ({
  milestone: m, expanded, onToggle, onOpenInsight,
  session, clubId, onReact, canPost, onCommentAdded,
  viewerId, viewerIsModerator, onEditPost, onDeletePost, onReportPost, onReplyToQuestion, onOpenQboxAdmin,
  dimmed = false,
}) => {
  const [milestonePosts, setMilestonePosts] = useState<any[] | null>(null);
  const [postsLoading, setPostsLoading] = useState(false);

  // Carrega posts deste marco quando expande pela primeira vez
  useEffect(() => {
    if (!expanded || milestonePosts !== null || !session || !clubId) return;
    let cancelled = false;
    setPostsLoading(true);
    (async () => {
      try {
        const r = await api.listPosts(session, clubId, { milestone: m.id, limit: 50 });
        if (!cancelled) {
          // Exclui o insight-final desse marco (já está no card compact acima)
          setMilestonePosts((r.posts || []).filter((p: any) => p.kind !== 'insight-final'));
        }
      } catch (e) {
        console.error('[milestone/posts]', e);
        if (!cancelled) setMilestonePosts([]);
      } finally {
        if (!cancelled) setPostsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [expanded, milestonePosts, session, clubId, m.id]);

  const totalPostsExcludingInsight = (m.totalPosts || 0) - (m.insightFinal ? 1 : 0);

  return (
    <div
      data-milestone-id={m.id}
      style={{
        // Atenua quando há atividade nova no marco corrente — efeito sutil que
        // empurra o histórico pro fundo visual sem esconder. Hover/expansão
        // restaura opacidade total (foco implícito).
        opacity: dimmed && !expanded ? 0.55 : 1,
        filter: dimmed && !expanded ? 'grayscale(0.25)' : 'none',
        transition: 'opacity 300ms ease, filter 300ms ease',
        scrollMarginTop: 70, // pra scrollIntoView não esconder header
      }}
      onMouseEnter={(e) => {
        if (dimmed) {
          (e.currentTarget as HTMLDivElement).style.opacity = '1';
          (e.currentTarget as HTMLDivElement).style.filter = 'none';
        }
      }}
      onMouseLeave={(e) => {
        if (dimmed && !expanded) {
          (e.currentTarget as HTMLDivElement).style.opacity = '0.55';
          (e.currentTarget as HTMLDivElement).style.filter = 'grayscale(0.25)';
        }
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0 16px' }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: 'var(--sage)', color: 'var(--paper)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 4px 10px -3px color-mix(in oklab, var(--sage) 60%, transparent)',
        }}>
          <Icon name="check" size={14} color="var(--paper)" stroke={3}/>
        </div>
        <div style={{ flex: 1, height: 0, borderTop: '1.5px dashed var(--line-strong)' }}/>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', borderRadius: 999,
          background: 'var(--paper-2)',
          border: '1px dashed var(--line-strong)',
          fontFamily: 'Geist Mono, monospace',
          fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase',
          color: 'var(--ink-3)', fontWeight: 600,
        }}>marco {m.n} · {m.label}</span>
      </div>
      <div className="mono" style={{
        fontSize: 9.5, color: 'var(--ink-4)', letterSpacing: '0.06em',
        marginBottom: 10, paddingLeft: 38,
      }}>encerrado em {m.closedOn ? new Date(m.closedOn).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }) : '—'}</div>
      {m.insightFinal && (
        <InsightFinalPostCard post={m.insightFinal}
          onOpen={() => onOpenInsight && onOpenInsight(m.insightFinal!)}
          compact
          clubId={clubId} session={session} canPost={canPost}
          onCommentAdded={onCommentAdded}
          viewerId={viewerId} viewerIsModerator={viewerIsModerator}
          onEditPost={onEditPost} onDeletePost={onDeletePost}/>
      )}
      {totalPostsExcludingInsight > 0 && (
        <button onClick={onToggle} style={{
          marginTop: 10, width: '100%',
          padding: '10px 14px', borderRadius: 12,
          background: 'var(--paper-2)', border: '1px dashed var(--line-strong)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          fontSize: 12, color: 'var(--ink-2)',
          fontFamily: 'Geist Mono, monospace', letterSpacing: '0.06em',
          cursor: 'pointer',
        }}>
          <span>{expanded ? '▼' : '▶'}</span>
          {expanded ? 'recolher' : 'ver feed completo'} · {totalPostsExcludingInsight} {totalPostsExcludingInsight === 1 ? 'post' : 'posts'}
        </button>
      )}
      {/* Feed completo do marco — só carrega quando user expande */}
      {expanded && (
        <div style={{
          marginTop: 12, paddingLeft: 12,
          borderLeft: '2px dashed var(--line-strong)',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          {postsLoading && (
            <div style={{ color: 'var(--ink-3)', fontSize: 12, padding: '8px 0' }}>
              Carregando histórico do marco…
            </div>
          )}
          {!postsLoading && milestonePosts && milestonePosts.length === 0 && (
            <div style={{ color: 'var(--ink-3)', fontSize: 12, fontStyle: 'italic', padding: '8px 0' }}>
              Sem outros posts neste marco — só o Insight Final.
            </div>
          )}
          {!postsLoading && milestonePosts && milestonePosts.map((p: any) => (
            <StudyPost key={p.id} post={p} onReact={onReact} onOpen={onOpenInsight}
              clubId={clubId} session={session} canPost={canPost} onCommentAdded={onCommentAdded}
              viewerId={viewerId} viewerIsModerator={viewerIsModerator}
              onEditPost={onEditPost} onDeletePost={onDeletePost}
              onReplyToQuestion={onReplyToQuestion}
              onOpenQboxAdmin={onOpenQboxAdmin}/>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Composer modal (escolha de tipo) ───
const ComposerModal: React.FC<{ onClose: () => void; isAdmin?: boolean; onPick?: (kind: string) => void }> = ({ onClose, isAdmin = false, onPick }) => {
  const [picked, setPicked] = useState<string | null>(null);
  // Pergunta (kind 'question') foi removida — Caixa de Perguntas (admin)
  // cobre o mesmo caso de uso de forma mais estruturada (anônima + responder).
  const types = [
    { kind: 'reflection',    title: 'Reflexão',         desc: 'Pensamento livre sobre o que leu' },
    { kind: 'citation',      title: 'Citação',          desc: 'Trecho que te marcou' },
    { kind: 'quiz',          title: 'Quiz',             desc: 'Múltipla escolha, com ou sem gabarito' },
    { kind: 'question-box',  title: 'Caixa de perguntas', desc: 'Receba dúvidas anônimas', adminOnly: true },
    { kind: 'insight-final', title: 'Insight Final',    desc: 'Encerra um marco', special: true },
  ];
  return (
    <>
      <div className="action-sheet-bg" onClick={onClose}/>
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0,
        background: 'var(--paper)',
        borderTopLeftRadius: 28, borderTopRightRadius: 28,
        padding: '14px 16px 28px',
        animation: 'slideInUp 320ms cubic-bezier(0.22, 1, 0.36, 1)',
        boxShadow: '0 -16px 48px rgba(0,0,0,0.25)',
        maxHeight: '80%', overflowY: 'auto',
      }}>
        <div style={{
          width: 40, height: 4, borderRadius: 2,
          background: 'var(--line-strong)', margin: '0 auto 14px',
        }}/>
        <div className="serif" style={{ fontSize: 24, lineHeight: 1.05, marginBottom: 4 }}>
          Como você quer <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>compartilhar</span>?
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 16 }}>
          Cada formato tem um lugar diferente no estudo do livro.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {types.filter(t => !t.adminOnly || isAdmin).map(t => {
            const meta = (POST_TYPES as any)[t.kind];
            const sel = picked === t.kind;
            return (
              <button key={t.kind} onClick={() => { setPicked(t.kind); onPick && onPick(t.kind); }} style={{
                width: '100%',
                padding: '14px 16px', borderRadius: 16,
                background: sel ? 'color-mix(in oklab, var(--ember) 12%, var(--paper-2))' : 'var(--paper-2)',
                border: sel ? '2px solid var(--ember)' : '1px solid var(--line)',
                display: 'flex', alignItems: 'center', gap: 14,
                textAlign: 'left', transition: 'all 160ms',
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: 'var(--paper-3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22, flexShrink: 0,
                }}>{meta.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 15, fontWeight: 500 }}>{t.title}</span>
                    {t.adminOnly && (
                      <span className="mono" style={{
                        fontSize: 8, padding: '2px 6px', borderRadius: 4,
                        background: 'var(--paper-3)', color: 'var(--ink-3)',
                        letterSpacing: '0.10em', textTransform: 'uppercase',
                      }}>líder</span>
                    )}
                    {t.special && (
                      <span className="mono" style={{
                        fontSize: 8, padding: '2px 6px', borderRadius: 4,
                        background: 'var(--ember)', color: 'var(--paper)',
                        letterSpacing: '0.10em', textTransform: 'uppercase', fontWeight: 600,
                      }}>marco</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 3, lineHeight: 1.4 }}>
                    {t.desc}
                  </div>
                </div>
                <Icon name="chevron-right" size={14} color="var(--ink-4)"/>
              </button>
            );
          })}
        </div>
        {picked && (
          <button onClick={onClose} style={{
            marginTop: 16, width: '100%',
            padding: '14px', borderRadius: 16,
            background: 'var(--ink)', color: 'var(--paper)',
            fontSize: 14, fontWeight: 500,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            Continuar para "{(POST_TYPES as any)[picked].label}"
            <Icon name="arrow-right" size={14} color="var(--paper)"/>
          </button>
        )}
      </div>
    </>
  );
};

const ComposerFAB: React.FC<{ onPick?: (kind?: string) => void }> = ({ onPick }) => (
  <button onClick={() => onPick && onPick()} style={{
    position: 'absolute', right: 16, bottom: 86,
    width: 56, height: 56, borderRadius: 28,
    background: 'var(--ember)', color: 'var(--paper)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 14px 28px -10px rgba(199,83,26,0.65)',
    zIndex: 30,
  }}>
    <Icon name="plus" size={22} color="var(--paper)" stroke={2.4}/>
  </button>
);

export const EstudoScreen: React.FC<{
  term?: 'clube' | 'grupo';
  clubId?: string;
  clubName?: string;
  isAdmin?: boolean;
  session?: any;
  // canPost = true se o viewer é membro (qualquer role) — controla se o input
  // de comentário aparece. Backend já valida; UI desabilita pra evitar erro.
  canPost?: boolean;
  // viewerId/viewerIsModerator: usados pra decidir se o menu "..." aparece em
  // cada post/comment. Membros editam/excluem o próprio; mods/admins gerenciam tudo.
  viewerId?: string;
  viewerIsModerator?: boolean;
  // Dados vindos do backend (App.tsx)
  milestones?: any[];
  posts?: any[];
  loading?: boolean;
  // Página atual do membro (do livro pessoal espelhado) — usada no header do marco
  yourPage?: number;
  // Filtro server-side: muda dispara nova carga
  filter?: string;
  onChangeFilter?: (filter: string) => void;
  onBack?: () => void;
  onMenu?: () => void;
  onCompose?: (kind?: string) => void;
  onReact?: (postId: string, kind: 'tocou' | 'aprendi' | 'fogo' | 'salvar') => Promise<void>;
  onOpenInsight?: (insight: MilestoneInsightFinal) => void;
  // Callback p/ atualizar count de comentários no posts state do App.tsx
  onCommentAdded?: (postId: string, delta: number) => void;
  // Edit/Delete: handlers fazem chamada API + atualizam o studyPosts state.
  onEditPost?: (postId: string, patch: any) => Promise<void>;
  onDeletePost?: (postId: string) => Promise<void>;
  // Reply: clicar "Compartilhar como você leu" num question post sobe pra App.tsx
  // que abre o ComposeSheet em modo reply.
  onReplyToQuestion?: (post: any) => void;
  /** Denuncia um post (UGC Apple Guideline 1.2). */
  onReportPost?: (post: any) => void;
  // Quando admin clica "Ver perguntas" no card de Caixa de Perguntas, App navega
  // pra ClubQuestionBoxAdminScreen no modo active da Caixa específica (postId).
  onOpenQboxAdmin?: (postId: string) => void;
  // Quando user vem do Insight Final detail clicando "Ver feed deste marco",
  // o App passa o id do marco aqui pra que ele já entre expandido + scrolla.
  // Após primeiro render usado, App reseta via onAfterInitialExpand.
  initialExpandedMilestoneId?: string | null;
  onAfterInitialExpand?: () => void;
  // Slot opcional pra o App.tsx injetar elementos no header (ex: sininho de pokes).
  // Aparece à direita do título, antes do botão "...".
  headerExtra?: React.ReactNode;
  storiesTray?: React.ReactNode;
}> = ({
  term = 'clube', clubId, clubName, session, canPost = true,
  viewerId, viewerIsModerator,
  isAdmin = false, milestones = [], posts = [], loading = false,
  yourPage = 0,
  filter = 'all', onChangeFilter,
  onBack, onMenu, onCompose, onReact, onOpenInsight, onCommentAdded,
  onEditPost, onDeletePost, onReportPost, onReplyToQuestion, onOpenQboxAdmin, headerExtra, storiesTray,
  initialExpandedMilestoneId, onAfterInitialExpand,
}) => {
  const T = useTerm(term);
  const [expandedMilestone, setExpandedMilestone] = useState<string | null>(initialExpandedMilestoneId || null);

  // Se App pediu pra abrir um marco específico expandido (drilldown do Insight),
  // setamos uma vez e avisamos o pai pra resetar (não abrir de novo na próxima volta).
  useEffect(() => {
    if (initialExpandedMilestoneId) {
      setExpandedMilestone(initialExpandedMilestoneId);
      // Scroll suave pro marco após render
      setTimeout(() => {
        const el = document.querySelector(`[data-milestone-id="${initialExpandedMilestoneId}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      onAfterInitialExpand?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialExpandedMilestoneId]);

  const active = milestones.find((m: any) => m.state === 'active');
  const closed = milestones
    .filter((m: any) => m.state === 'closed')
    .sort((a: any, b: any) => b.n - a.n); // mais recente primeiro

  // Filtros server-side. Posts já vêm sem deleted, mas vou filtrar por marco
  // no client pra implementar a regra:
  // - Tem marco ativo → mostra só posts desse marco no feed principal
  //   (posts dos marcos anteriores ficam recolhidos em "Marcos anteriores")
  // - Não tem marco ativo (último marco fechou e admin não criou próximo) →
  //   mostra o Insight Final do último marco fechado em destaque, sem outros posts
  const setFilter = (f: string) => onChangeFilter && onChangeFilter(f);

  const visiblePosts = useMemo(() => {
    if (filter !== 'all' && filter !== 'finals') return posts; // outros filtros são livres
    if (filter === 'finals') return []; // mostrado em "Marcos anteriores"
    if (active) {
      // Mostra posts vinculados ao marco ativo (ou sem marco — fallback)
      return posts.filter((p: any) => !p.milestoneId || p.milestoneId === active.id);
    }
    // Sem marco ativo: o feed principal "vazio" mostra só o Insight do último closed.
    // Isso é exibido na seção própria abaixo (renderiza closed[0].insightFinal grande).
    return [];
  }, [posts, active, filter]);

  // Se não há marco ativo MAS há closed mais recente → mostra ele em "destaque"
  // no feed principal (não em "Marcos anteriores"). Quando alguém posta no
  // próximo marco, esse insight migra automaticamente pra "Marcos anteriores".
  const featuredInsight = !active && closed.length > 0 ? closed[0].insightFinal : null;
  // Closed milestones a renderizar como histórico ("Marcos anteriores")
  const closedToHistory = !active && closed.length > 0
    ? closed.slice(1) // pula o mais recente (já está em destaque)
    : closed;

  return (
    <div className="scroll" style={{ paddingBottom: 160, position: 'relative' }}>
      <div style={{
        padding: '58px 20px 14px', background: 'var(--paper)',
        position: 'sticky', top: 0, zIndex: 10,
        borderBottom: '1px solid var(--line)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onBack} style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'var(--paper-2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid var(--line)',
          }}>
            <Icon name="chevron-left" size={16}/>
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="mono" style={{
              fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase',
              color: 'var(--ink-3)',
            }}>{clubName || ''}</div>
            <div className="serif" style={{
              fontSize: 24, lineHeight: 1.05, marginTop: 2, letterSpacing: '-0.005em',
            }}>
              Estudo <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>do livro</span>
            </div>
          </div>
          {headerExtra}
          <button onClick={onMenu} style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'var(--paper-2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid var(--line)',
          }}>
            <Icon name="more" size={16}/>
          </button>
        </div>

        <div style={{
          marginTop: 14, display: 'flex', gap: 6,
          overflowX: 'auto', marginLeft: -20, marginRight: -20,
          paddingLeft: 20, paddingRight: 20,
        }}>
          {STUDY_FILTERS.map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)} style={{
              flexShrink: 0,
              padding: '6px 12px', borderRadius: 999,
              background: filter === f.id ? 'var(--ink)' : 'var(--paper-2)',
              color: filter === f.id ? 'var(--paper)' : 'var(--ink-2)',
              border: filter === f.id ? '1px solid var(--ink)' : '1px solid var(--line-strong)',
              fontSize: 12, fontWeight: 500,
              display: 'inline-flex', alignItems: 'center', gap: 5,
              transition: 'all 160ms',
            }}>
              {f.icon && <span style={{ fontSize: 12 }}>{f.icon}</span>}
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {storiesTray}

      {active && filter !== 'finals' && (
        <div style={{ padding: '18px 20px 0' }}>
          <ActiveMilestoneHeader milestone={active} yourPage={yourPage}/>
        </div>
      )}

      {filter !== 'finals' && (
        <div style={{ padding: '16px 20px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {loading && posts.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
              Carregando feed…
            </div>
          )}

          {/* Insight Final em destaque (sem marco ativo novo ainda) */}
          {!loading && featuredInsight && (
            <InsightFinalPostCard
              post={featuredInsight}
              onReact={onReact}
              onOpen={() => onOpenInsight && onOpenInsight(featuredInsight)}
              clubId={clubId} session={session} canPost={canPost}
              onCommentAdded={onCommentAdded}
              viewerId={viewerId} viewerIsModerator={viewerIsModerator}
              onEditPost={onEditPost} onDeletePost={onDeletePost}
            />
          )}

          {/* Posts do marco ativo */}
          {!loading && visiblePosts.map((post: any) => (
            <StudyPost key={post.id} post={post} onReact={onReact} onOpen={onOpenInsight}
              clubId={clubId} session={session} canPost={canPost} onCommentAdded={onCommentAdded}
              viewerId={viewerId} viewerIsModerator={viewerIsModerator}
              onEditPost={onEditPost} onDeletePost={onDeletePost}
              onReplyToQuestion={onReplyToQuestion}
              onOpenQboxAdmin={onOpenQboxAdmin}/>
          ))}

          {/* Empty states */}
          {!loading && !featuredInsight && visiblePosts.length === 0 && active && (
            <div style={{
              padding: 28, textAlign: 'center',
              border: '1px dashed var(--line-strong)',
              borderRadius: 18,
              color: 'var(--ink-3)', fontSize: 13,
            }}>
              <div className="serif" style={{ fontSize: 18, fontStyle: 'italic', marginBottom: 4 }}>
                Marco ativo, sem posts ainda
              </div>
              <div>Seja a primeira pessoa a postar — toque no botão "Compartilhar".</div>
            </div>
          )}
          {!loading && !featuredInsight && visiblePosts.length === 0 && !active && closed.length === 0 && (
            <div style={{
              padding: 28, textAlign: 'center',
              border: '1px dashed var(--line-strong)',
              borderRadius: 18,
              color: 'var(--ink-3)', fontSize: 13,
            }}>
              <div className="serif" style={{ fontSize: 18, fontStyle: 'italic', marginBottom: 4 }}>
                Nada por aqui ainda
              </div>
              <div>O líder ainda não definiu marcos. Convide o líder a criar o primeiro pelo menu (...).</div>
            </div>
          )}
        </div>
      )}

      {filter === 'all' && closedToHistory.length > 0 && (
        <div style={{ padding: '28px 20px 6px' }}>
          <div className="label">Marcos anteriores</div>
        </div>
      )}

      {(filter === 'all' || filter === 'finals') && (
        <div style={{
          padding: filter === 'finals' ? '24px 20px 0' : '8px 20px 0',
          display: 'flex', flexDirection: 'column', gap: 18,
        }}>
          {filter === 'finals' && closed.length > 0 && (
            <div className="serif" style={{ fontSize: 22, lineHeight: 1.1, marginBottom: 4, padding: '0 4px' }}>
              <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>Insights finais</span><br/>
              de cada marco
            </div>
          )}
          {/* No filtro 'finals' mostra TODOS os closed; no 'all' usa closedToHistory
              (pula o mais recente quando ele já é o featured destaque acima). */}
          {(filter === 'finals' ? closed : closedToHistory).map((m: any) => (
            <ClosedMilestone key={m.id} milestone={m}
              expanded={expandedMilestone === m.id}
              onToggle={() => setExpandedMilestone(e => e === m.id ? null : m.id)}
              onOpenInsight={onOpenInsight}
              session={session}
              clubId={clubId}
              onReact={onReact}
              canPost={canPost}
              onCommentAdded={onCommentAdded}
              viewerId={viewerId} viewerIsModerator={viewerIsModerator}
              onEditPost={onEditPost} onDeletePost={onDeletePost}
              onReplyToQuestion={onReplyToQuestion}
              onOpenQboxAdmin={onOpenQboxAdmin}
              // Atenua quando há marco ativo com posts publicados
              // (regra: ao postar no marco novo, o anterior fica sutil)
              dimmed={!!active && visiblePosts.length > 0 && filter === 'all'}/>
          ))}
        </div>
      )}

      <div style={{ padding: '40px 20px 20px', textAlign: 'center' }}>
        <div className="mono" style={{
          fontSize: 9.5, letterSpacing: '0.16em',
          color: 'var(--ink-4)', textTransform: 'uppercase',
        }}>✦ início da jornada ✦</div>
        <div className="serif" style={{
          fontSize: 14, fontStyle: 'italic',
          color: 'var(--ink-3)', marginTop: 6,
        }}>Você abriu este {T.s} em 8 de abril.</div>
      </div>

      {/* FAB renderizado pelo App.tsx (fora do scroll), pra ser fixo na viewport. */}
    </div>
  );
};

// ─── Insight Final detail screen ───

export const InsightFinalDetailScreen: React.FC<{
  insight: MilestoneInsightFinal | null;
  session?: any;
  clubId?: string;
  onBack: () => void;
  // Lista de marcos encerrados (ordenada do mais recente pro mais antigo) usada
  // pra navegação cronológica entre Insights Finais. App.tsx passa studyMilestones
  // já filtrados por state='closed' com insightFinal presente.
  closedMilestones?: any[];
  onOpenInsight?: (insight: any) => void;
  // Volta pra tela de Estudo focando o feed do marco específico (drilldown)
  onOpenMilestoneFeed?: (milestoneId: string) => void;
}> = ({ insight, session, clubId, onBack, closedMilestones = [], onOpenInsight, onOpenMilestoneFeed }) => {
  const [comments, setComments] = useState<any[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);

  // Posição cronológica nos marcos encerrados — pra navegação ← anterior · próximo →
  const insightId = insight?.id;
  const orderedAsc = [...closedMilestones].filter(m => m.insightFinal?.id).sort((a, b) => (a.n || 0) - (b.n || 0));
  const currentIdx = insightId ? orderedAsc.findIndex(m => m.insightFinal?.id === insightId) : -1;
  const prevMilestone = currentIdx > 0 ? orderedAsc[currentIdx - 1] : null;
  const nextMilestone = currentIdx >= 0 && currentIdx < orderedAsc.length - 1 ? orderedAsc[currentIdx + 1] : null;
  const currentMilestone = currentIdx >= 0 ? orderedAsc[currentIdx] : null;

  useEffect(() => {
    if (!session || !clubId || !insight?.id) return;
    let cancelled = false;
    setLoadingComments(true);
    (async () => {
      try {
        const r = await api.listComments(session, clubId, insight.id);
        if (!cancelled) setComments(r.comments || []);
      } catch (e) {
        console.error('[insight/comments]', e);
      } finally {
        if (!cancelled) setLoadingComments(false);
      }
    })();
    return () => { cancelled = true; };
  }, [session, clubId, insight?.id]);

  const handlePostComment = async () => {
    if (!session || !clubId || !insight?.id) return;
    const text = draft.trim();
    if (!text) return;
    setPosting(true);
    try {
      const r = await api.createComment(session, clubId, insight.id, text);
      setComments(prev => [...prev, r.comment]);
      setDraft('');
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao comentar');
    } finally {
      setPosting(false);
    }
  };

  if (!insight) return null;
  return (
    <div className="scroll" style={{ background: 'var(--insight-detail-bg)' }}>
      <div style={{ padding: '54px 20px 0' }}>
        <button onClick={onBack} style={{
          width: 36, height: 36, borderRadius: '50%',
          background: 'var(--paper-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid var(--line)',
          backdropFilter: 'blur(6px)',
        }}>
          <Icon name="chevron-left" size={16}/>
        </button>
      </div>
      <div style={{ padding: '20px 20px 0' }}>
        {/* Sub-header de contexto: nº do marco + data + página + posição cronológica */}
        <div className="mono" style={{
          fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: 'var(--ember)', fontWeight: 600,
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}>
          ✦ Insight Final · {insight.milestoneLabel}
        </div>
        {currentMilestone && (
          <div className="mono" style={{
            marginTop: 6, fontSize: 9.5, letterSpacing: '0.10em',
            color: 'var(--ink-3)',
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
          }}>
            <span>Marco {currentMilestone.n} de {orderedAsc.length}</span>
            {currentMilestone.closedOn && (
              <>
                <span style={{ color: 'var(--line-strong)' }}>·</span>
                <span>encerrado em {new Date(currentMilestone.closedOn).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })}</span>
              </>
            )}
            {currentMilestone.targetPage && (
              <>
                <span style={{ color: 'var(--line-strong)' }}>·</span>
                <span>pág {currentMilestone.targetPage}</span>
              </>
            )}
          </div>
        )}
        <div className="serif" style={{
          fontSize: 38, lineHeight: 1.05, marginTop: 14, letterSpacing: '-0.01em',
        }}>{insight.title || 'Marco encerrado'}</div>
        <div style={{ marginTop: 22, display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar letter={insight.author?.avatar || '?'} size={44}/>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 15, fontWeight: 500 }}>{insight.author?.name || 'Membro'}</span>
              {insight.author?.verified && (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="var(--ember)">
                  <path d="M12 2l2.4 1.8 3 .2.6 3 2 2.2-1.4 2.7.4 3-2.7 1.4-1.4 2.7-3-.4-2.7 1.4-2.2-2-3-.6-.2-3L2 12l1.8-2.4 .2-3 3-.6 2.2-2L12 2z"/>
                  <path d="M9 12l2 2 4-4" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <div className="mono" style={{
              fontSize: 10.5, color: 'var(--ink-3)',
              letterSpacing: '0.06em', marginTop: 2,
            }}>
              {insight.author?.role && `${insight.author.role === 'Admin' ? 'Líder' : insight.author.role} · `}publicado quando alcançou pág {insight.page}
            </div>
          </div>
        </div>
        <div className="serif" style={{
          fontSize: 20, lineHeight: 1.5, marginTop: 28,
          color: 'var(--ink)', textWrap: 'pretty',
        }}>{insight.text}</div>
        <div style={{ marginTop: 28 }}>
          <InteractiveReactionRow postId={insight.id} reactions={insight.reactions} yourReactions={(insight as any).yourReactions || []}/>
        </div>

        {/* CTA: ver feed completo deste marco (drilldown pro ClosedMilestone expandido) */}
        {currentMilestone && onOpenMilestoneFeed && (
          <button
            onClick={() => onOpenMilestoneFeed(currentMilestone.id)}
            style={{
              marginTop: 22, width: '100%',
              padding: '12px 16px', borderRadius: 14,
              background: 'var(--paper-2)', border: '1px solid var(--line)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              cursor: 'pointer', transition: 'all 160ms',
              fontFamily: 'inherit', textAlign: 'left',
            }}
          >
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--ink)' }}>
                Ver feed deste marco
              </div>
              <div className="mono" style={{
                fontSize: 9.5, letterSpacing: '0.06em', color: 'var(--ink-3)', marginTop: 2,
              }}>
                {currentMilestone.totalPosts || 0} {(currentMilestone.totalPosts || 0) === 1 ? 'post' : 'posts'} no marco
              </div>
            </div>
            <Icon name="chevron-right" size={14} color="var(--ink-3)"/>
          </button>
        )}

        {/* Navegação cronológica entre Insights Finais */}
        {(prevMilestone || nextMilestone) && (
          <div style={{
            marginTop: 16, display: 'flex', gap: 8,
          }}>
            <button
              onClick={() => prevMilestone?.insightFinal && onOpenInsight?.(prevMilestone.insightFinal)}
              disabled={!prevMilestone}
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 12,
                background: prevMilestone ? 'var(--paper-2)' : 'transparent',
                border: '1px solid ' + (prevMilestone ? 'var(--line)' : 'var(--paper-3)'),
                cursor: prevMilestone ? 'pointer' : 'default',
                opacity: prevMilestone ? 1 : 0.4,
                display: 'flex', alignItems: 'center', gap: 8,
                fontFamily: 'inherit', textAlign: 'left',
              }}
            >
              <Icon name="chevron-left" size={12} color="var(--ink-3)"/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="mono" style={{
                  fontSize: 8.5, letterSpacing: '0.10em', textTransform: 'uppercase',
                  color: 'var(--ink-4)',
                }}>marco anterior</div>
                <div style={{
                  fontSize: 12, color: 'var(--ink-2)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{prevMilestone?.label || '—'}</div>
              </div>
            </button>
            <button
              onClick={() => nextMilestone?.insightFinal && onOpenInsight?.(nextMilestone.insightFinal)}
              disabled={!nextMilestone}
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 12,
                background: nextMilestone ? 'var(--paper-2)' : 'transparent',
                border: '1px solid ' + (nextMilestone ? 'var(--line)' : 'var(--paper-3)'),
                cursor: nextMilestone ? 'pointer' : 'default',
                opacity: nextMilestone ? 1 : 0.4,
                display: 'flex', alignItems: 'center', gap: 8,
                fontFamily: 'inherit', textAlign: 'right',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="mono" style={{
                  fontSize: 8.5, letterSpacing: '0.10em', textTransform: 'uppercase',
                  color: 'var(--ink-4)',
                }}>próximo marco</div>
                <div style={{
                  fontSize: 12, color: 'var(--ink-2)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{nextMilestone?.label || '—'}</div>
              </div>
              <Icon name="chevron-right" size={12} color="var(--ink-3)"/>
            </button>
          </div>
        )}

        <div style={{
          marginTop: 32, padding: '14px 0',
          borderTop: '1px solid var(--line)',
          borderBottom: '1px solid var(--line)',
        }}>
          <div className="serif" style={{ fontSize: 18 }}>
            {comments.length} {comments.length === 1 ? 'comentário' : 'comentários'}
          </div>
        </div>
        <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 14, paddingBottom: 130 }}>
          {loadingComments && comments.length === 0 && (
            <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: '12px 4px' }}>
              Carregando…
            </div>
          )}
          {!loadingComments && comments.length === 0 && (
            <div style={{
              padding: 20, textAlign: 'center',
              border: '1px dashed var(--line-strong)',
              borderRadius: 14,
              color: 'var(--ink-3)', fontSize: 13,
            }}>
              Nenhum comentário ainda. Seja o primeiro a reagir.
            </div>
          )}
          {comments.map((c: any) => (
            <div key={c.id} style={{ display: 'flex', gap: 10 }}>
              <Avatar letter={c.author?.avatar || '?'} size={32}/>
              <div style={{ flex: 1 }}>
                <div style={{
                  padding: '10px 14px', borderRadius: 16, borderTopLeftRadius: 4,
                  background: 'var(--paper-2)',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>{c.author?.name || 'Membro'}</div>
                  <div style={{ fontSize: 13.5, lineHeight: 1.45, color: 'var(--ink-2)', whiteSpace: 'pre-wrap' }}>{c.text}</div>
                </div>
                <div className="mono" style={{
                  fontSize: 10, color: 'var(--ink-4)',
                  letterSpacing: '0.06em', marginTop: 4, paddingLeft: 14,
                }}>{c.timeAgo}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0,
        padding: '12px 16px 30px',
        background: 'linear-gradient(to top, var(--paper) 70%, rgba(245,241,234,0))',
        display: 'flex', alignItems: 'center', gap: 8,
        maxWidth: 480, margin: '0 auto',
        zIndex: 40,
      }}>
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePostComment(); } }}
          placeholder="Comente esse insight..."
          style={{
            flex: 1,
            padding: '12px 16px', borderRadius: 999,
            background: 'var(--paper-2)', border: '1px solid var(--line)',
            fontSize: 14, outline: 'none', fontFamily: 'inherit', color: 'var(--ink)',
          }}
        />
        <button onClick={handlePostComment} disabled={!draft.trim() || posting} style={{
          width: 44, height: 44, borderRadius: '50%',
          background: !draft.trim() || posting ? 'var(--paper-3)' : 'var(--ember)',
          color: 'var(--paper)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: 'none', cursor: !draft.trim() || posting ? 'not-allowed' : 'pointer',
        }}>
          <Icon name="arrow-right" size={16} color="var(--paper)" stroke={2.4}/>
        </button>
      </div>
    </div>
  );
};
